import { Box, Dialog, DialogActions, DialogContent, Grid, TextField } from "@mui/material";
import { MprcConfirmDialog, MprcDialogTitleClose, MprcLink, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonConfirm } from "components/buttons";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { AutoRegisterSettingsResponse } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useCheckPassword from "views/foundation/user/hooks/useCheckPassword";

////////////////////////////////////////////////////////////////////////////////
interface IPros {
    onClose: (success: boolean) => void;
}

interface IFields {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface IValidationFields {
    oldPassword: string[] | null | undefined;
    newPassword: string[] | null | undefined;
    confirmPassword: string[] | null | undefined;
}

/////////////////////////////////////////////////////////////////////////////////

export const UserChangePasswordDialog: React.FC<IPros> = (props) => {
    const api = useCoreApi();
    const session = useSession();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { onClose } = props;

    const [settings, setSettings] = useState<AutoRegisterSettingsResponse>(null!);
    const [appTwoFactorActived, setAppTwoFactorActived] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [resetTwoFactor, setResetTwoFactor] = useState<boolean>(false);

    const [viewError, setViewError] = useState<any>();

    const { checkPassword } = useCheckPassword(settings, session.user.name);

    const [register, setRegister] = useState<IFields>({
        newPassword: "",
        oldPassword: "",
        confirmPassword: ""
    });

    const inputRef = useRef<HTMLInputElement>(undefined);

    const formValidation = useRef<IValidationFields>({
        oldPassword: undefined,
        newPassword: undefined,
        confirmPassword: undefined,
    });

    ////////////////////////////////////////////////////////////////////////////
    ///
    const handleChangePassword = async () => {
        formValidation.current = { oldPassword: null, newPassword: null, confirmPassword: null };

        try {
            setUpdating(true);
            var result = await api.v1.user.changePassword(register);
            if (result.data) {
                enqueueSnackbar(t("User.UserChangePasswordDialog.PasswordChanged"), { variant: "success" });
                onClose(true);
            }
            else {
                enqueueSnackbar(t("Messages.ApiCalledRefused", { ns: "shared" }), { variant: "error" });
            }
        } catch (e) {
            var r = MprcResolveRequestErrors(e);

            Object.entries(r.fieldsWithError ?? {}).forEach(([key, value]) => {
                if (formValidation.current.hasOwnProperty(key)) {
                    (formValidation.current as any)[key] = value;
                }
            });

            setViewError(r.otherErrors);
            inputRef.current?.focus();
        } finally {
            setUpdating(false);
        }
    };

    ///
    const doResetTwoFactor = async () => {
        setUpdating(true);
        try {
            var response = await api.v1.account.appAuthenticatorReset({});
            if (response.data) {
                enqueueSnackbar(t("User.ChangePwdDialog.AppTwoFactorResetSuccessfull"), { variant: "success" });
                setAppTwoFactorActived(false);
            }
            else {
                enqueueSnackbar(t("User.ChangePwdDialog.AppTwoFactorResetFailed"), { variant: "error" });
            }
        }
        catch {
            enqueueSnackbar(t("shared:Error.PostFailed"), { variant: "error" });
        }
        finally {
            setUpdating(false);
        }
    };

    ///
    function handleUpdateRecord(name: keyof IFields, v: any) {

        const newState = { ...register, [name]: v };
        const requiredMessage = [t("Auth.Register.RequiredField")];

        if (formValidation.current.hasOwnProperty(name)) {

            switch (name) {
                case "newPassword":
                    const np = checkPassword(newState.newPassword);
                    formValidation.current.newPassword = (np.length == 0) ? null : np;

                    if (newState.confirmPassword.length > 0) {
                        formValidation.current.confirmPassword = (newState.confirmPassword != newState.newPassword)
                            ? [t("Auth.Register.ConfirmPassWrong")]
                            : null;
                    }
                    break;

                case "confirmPassword":
                    formValidation.current.confirmPassword =
                        v == ""
                            ? requiredMessage
                            : (newState.confirmPassword != newState.newPassword)
                                ? [t("Auth.Register.ConfirmPassWrong")]
                                : null;
                    break;

                default:
                    formValidation.current = { ...formValidation.current, [name]: v == "" ? requiredMessage : null };
                    break;
            }
        }

        setRegister(newState);
    }

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        function getData() {
            Promise.all([api.v1.account.autoRegisterSettings(), api.v1.account.getCurrentAppAuthenticatorInfo()])
                .then((results) => {
                    setSettings(results[0].data);
                    setAppTwoFactorActived(results[1].data.twoFactorAppActived);
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        if (loading) getData();
        else setTimeout(() => inputRef.current?.focus(), 100);

    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////
    const canSave = Object.entries(formValidation.current).filter(([key, v]) => v !== null).length == 0;
    const disabled = updating || loading;

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={() => onClose(false)}>
            <MprcDialogTitleClose
                title={t("User.ChangePwdDialog.Title")}
                subtitle={session.user.name}
                onClose={() => onClose(false)}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestErrorReport error={viewError} />
                <MprcRequestIndicator type={loading ? "request" : "post"} visible={loading || updating} />

                <Grid container spacing={2} direction="column">
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            autoFocus
                            label={t("User.UserChangePasswordDialog.OldPasswordLabel")}
                            fullWidth
                            required
                            type="password"
                            disabled={disabled}
                            autoComplete="off"
                            error={!!formValidation.current.oldPassword}
                            helperText={<MprcStringToHtml text={formValidation.current.oldPassword} />}
                            inputRef={inputRef}
                            value={register.oldPassword}
                            onChange={(e) => handleUpdateRecord("oldPassword", e.target.value)}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 }
                                }
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label={t("User.UserChangePasswordDialog.NewPasswordLabel")}
                            fullWidth
                            required
                            type="password"
                            disabled={disabled}
                            autoComplete="off"
                            value={register.newPassword}
                            onChange={(e) => handleUpdateRecord("newPassword", e.target.value)}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 }
                                }
                            }}
                            error={!!formValidation.current.newPassword}
                            helperText={<MprcStringToHtml text={formValidation.current.newPassword} />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label={t("Auth.Register.PasswordConfirmLabel")}
                            fullWidth
                            required
                            type="password"
                            disabled={disabled}
                            autoComplete="off"
                            value={register.confirmPassword}
                            onChange={(e) => handleUpdateRecord("confirmPassword", e.target.value)}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 }
                                }
                            }}
                            error={!!formValidation.current.confirmPassword}
                            helperText={<MprcStringToHtml text={formValidation.current.confirmPassword} />}
                        />
                    </Grid>
                </Grid>

                {
                    appTwoFactorActived &&
                    <Box sx={{ mt: 4 }}>
                        <MprcLink onClick={() => setResetTwoFactor(true)}>
                            <span>&#9654;&nbsp;</span>
                            {t("User.ChangePwdDialog.AppTwoFactorResetText")}
                        </MprcLink>
                    </Box>
                }

                <MprcConfirmDialog
                    open={resetTwoFactor}
                    message={t("User.ChangePwdDialog.AppTwoFactorResetConfirmText")}
                    yesText={t("shared:Literal.Continue")}
                    noText={t("shared:Literal.Cancel")}
                    onClose={(confirm) => {
                        setResetTwoFactor(false);
                        if (confirm) doResetTwoFactor();
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
                <ButtonConfirm
                    text={t("User.NotificationConfigDialog.ButtonChangePasswordText")}
                    title={t("User.ChangePwdDialog.ConfirmButtonTooltip")}
                    variant="contained"
                    disabled={!canSave || disabled}
                    onClick={handleChangePassword}
                />
            </DialogActions>
        </Dialog>
    );
};
